import { DEFAULT_CATEGORY_ORDER } from "@shared/categories";
import { createFileRoute } from "@tanstack/react-router";
import { useLiveQuery } from "dexie-react-hooks";
import { LayoutGrid, List } from "lucide-react";
import { SortableCategoryList } from "@/components/SortableCategoryList";
import { Spinner } from "@/components/Spinner";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Toggle } from "@/components/ui/toggle";
import { ToggleGroup } from "@/components/ui/toggle-group";
import { db } from "@/lib/db";
import { setShoppingLayout, useShoppingLayout } from "@/lib/shopping-layout";
import {
  setShowArchivedRecipes,
  useShowArchivedRecipes,
} from "@/lib/show-archived-recipes";
import {
  resetCategoryOrder,
  updateCategoryOrder,
} from "@/lib/sync/change-tracker";

function SettingsPage() {
  const shoppingLayout = useShoppingLayout();
  const showArchivedRecipes = useShowArchivedRecipes();
  const settingsRows = useLiveQuery(() =>
    db.userSettings.where("syncId").equals("user-settings-singleton").toArray(),
  );

  if (!settingsRows) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  const categoryOrder =
    settingsRows[0]?.categoryOrder ?? DEFAULT_CATEGORY_ORDER;

  return (
    <div className="w-full max-w-xl space-y-8">
      <section className="space-y-3">
        <h2 className="px-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Shopping
        </h2>
        <Card className="gap-0 divide-y divide-foreground/10 py-0">
          <div className="space-y-3 p-4">
            <div className="space-y-0.5">
              <div className="font-medium">Shopping list layout</div>
              <p className="text-sm text-muted-foreground">
                Choose how items appear on your shopping list. This device only.
              </p>
            </div>
            <ToggleGroup
              value={[shoppingLayout]}
              onValueChange={(value) => {
                // base-ui emits the full pressed set; ignore an empty set
                // (which happens if the active item is re-clicked) so a layout
                // stays chosen.
                const next = value[0];
                if (next === "tiles" || next === "list") {
                  setShoppingLayout(next);
                }
              }}
            >
              <Toggle value="tiles" aria-label="Tiles">
                <LayoutGrid className="size-4" />
                Tiles
              </Toggle>
              <Toggle value="list" aria-label="List">
                <List className="size-4" />
                List
              </Toggle>
            </ToggleGroup>
          </div>
          <div className="space-y-3 p-4">
            <div className="space-y-0.5">
              <div className="font-medium">Category order</div>
              <p className="text-sm text-muted-foreground">
                Drag to reorder how categories appear on your shopping list.
              </p>
            </div>
            <SortableCategoryList
              items={categoryOrder}
              onReorder={updateCategoryOrder}
            />
            <button
              type="button"
              onClick={resetCategoryOrder}
              className="text-sm text-muted-foreground underline underline-offset-2"
            >
              Reset to default order
            </button>
          </div>
        </Card>
      </section>

      <section className="space-y-3">
        <h2 className="px-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Recipes
        </h2>
        <Card className="py-0">
          <div className="flex items-center justify-between gap-4 p-4">
            <div className="space-y-0.5">
              <label
                htmlFor="show-archived-recipes"
                className="cursor-pointer font-medium"
              >
                Show archived recipes
              </label>
              <p
                id="show-archived-recipes-desc"
                className="text-sm text-muted-foreground"
              >
                Include archived recipes in the recipes list and search. This
                device only; archived recipes are always hidden from the meal
                planner and recipe pickers.
              </p>
            </div>
            <Switch
              id="show-archived-recipes"
              aria-describedby="show-archived-recipes-desc"
              checked={showArchivedRecipes}
              onCheckedChange={setShowArchivedRecipes}
            />
          </div>
        </Card>
      </section>
    </div>
  );
}

export const Route = createFileRoute("/_settings/settings")({
  component: SettingsPage,
  staticData: { title: "Settings" },
});
